import { useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isNull, merge, omitBy } from 'lodash';

import { RecursivePartial } from '@pimm/base';
import { isFutureDate, isValidMonthDayOrEmpty, isValidOrEmptyDate, PHONE_REGEX } from '../utils';

// Role, Import Sync Mode, and Employee ID (the Importer Sync dialog's hosted
// input) are staged as plain component state in AddEmployeeModal, same split
// MemberProfileModal already uses for its FieldSelectDialog-driven fields —
// they're not part of this react-hook-form schema.
export const FormAddEmployeeSchema = yup.object({
  firstName: yup.string().required('store_settings:first_name_required').default(''),
  lastName: yup.string().required('store_settings:last_name_required').default(''),
  title: yup.string().optional().default(''),
  // Birthday is year-less (MM / DD only, no future check — a birthday
  // recurs every year, so "future" isn't a meaningful constraint without a
  // year to compare against). Hire Date keeps the full masked date below.
  birthDate: yup
    .string()
    .optional()
    .default('')
    .test('valid-birthday', 'store_settings:invalid_birthday', v => isValidMonthDayOrEmpty(v ?? '')),
  hireDate: yup
    .string()
    .optional()
    .default('')
    .test('valid-date', 'store_settings:invalid_date', v => isValidOrEmptyDate(v ?? ''))
    .test('not-future', 'store_settings:date_future', v => !isFutureDate(v ?? '')),
  email: yup.string().optional().email('store_settings:invalid_email').default(''),
  // International-friendly: allowed characters (PHONE_REGEX) plus a 7-15
  // digit count (E.164 range), not a fixed US length — rejects obvious junk
  // like "12" or "(((" while accepting any real US or international number.
  mobileNumber: yup
    .string()
    .optional()
    .default('')
    .matches(PHONE_REGEX, { message: 'store_settings:invalid_phone', excludeEmptyString: true })
    .test('phone-digit-count', 'store_settings:invalid_phone', v => {
      if (!v) return true;
      const digits = v.replace(/\D/g, '').length;
      return digits >= 7 && digits <= 15;
    }),
});

export type FormDataAddEmployee = yup.InferType<typeof FormAddEmployeeSchema>;
export type FormAddEmployeeReturn = UseFormReturn<FormDataAddEmployee>;

export type FormAddEmployeeProps = {
  defaultValues?: RecursivePartial<FormDataAddEmployee>;
};

export const useFormAddEmployee = (props?: FormAddEmployeeProps): FormAddEmployeeReturn => {
  return useForm<FormDataAddEmployee>({
    defaultValues: merge(FormAddEmployeeSchema.getDefault(), omitBy(props?.defaultValues, isNull)),
    mode: 'onTouched',
    resolver: yupResolver(FormAddEmployeeSchema),
  });
};
